import React, { useRef, useState } from 'react';

let suppressTimeCommit = false;

/** Drop the blur-commit that fires when Escape unmounts a focused time field. */
export function ignoreNextTimeCommit() {
  suppressTimeCommit = true;
  window.setTimeout(() => {
    suppressTimeCommit = false;
  }, 0);
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function digitsOnly(raw) {
  return String(raw).replace(/\D/g, '').slice(0, 2);
}

/**
 * Hour and minute boxes. `value` is "HH:mm" from react-datepicker; `onChange`
 * receives the same shape.
 */
export default function TimeFields({ value = '', onChange }) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value || '');
  const hourShown = match ? pad2(Math.min(23, Number(match[1]))) : '';
  const minuteShown = match ? pad2(Math.min(59, Number(match[2]))) : '';
  const [hourDraft, setHourDraft] = useState(null);
  const [minuteDraft, setMinuteDraft] = useState(null);
  const hourRef = useRef(null);
  const minuteRef = useRef(null);
  const skipBlurRef = useRef(false);

  const commit = (hours, minutes) => {
    if (suppressTimeCommit) {
      suppressTimeCommit = false;
      return;
    }
    const hh = Math.min(23, hours == null ? (hourShown === '' ? 0 : Number(hourShown)) : hours);
    const mm = Math.min(59, minutes == null ? (minuteShown === '' ? 0 : Number(minuteShown)) : minutes);
    onChange?.(`${pad2(hh)}:${pad2(mm)}`);
  };

  const onHourChange = (e) => {
    const digits = digitsOnly(e.target.value);
    setHourDraft(digits);
    if (digits.length === 2) {
      skipBlurRef.current = true;
      const hours = Math.min(23, Number(digits));
      setHourDraft(null);
      commit(hours, null);
      minuteRef.current?.focus();
      minuteRef.current?.select();
    }
  };

  const onMinuteChange = (e) => {
    const digits = digitsOnly(e.target.value);
    setMinuteDraft(digits);
    if (digits.length === 2) {
      const minutes = Math.min(59, Number(digits));
      setMinuteDraft(null);
      commit(null, minutes);
    }
  };

  const bump = (which, delta) => {
    const max = which === 'hour' ? 23 : 59;
    const current = which === 'hour'
      ? (hourDraft != null ? hourDraft : hourShown)
      : (minuteDraft != null ? minuteDraft : minuteShown);
    const base = current === '' ? 0 : Number(current);
    const next = (base + delta + max + 1) % (max + 1);
    if (which === 'hour') {
      setHourDraft(null);
      commit(next, null);
    } else {
      setMinuteDraft(null);
      commit(null, next);
    }
  };

  const onKeyDown = (which) => (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      bump(which, 1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      bump(which, -1);
    } else if (e.key === 'Backspace' && which === 'minute' && (minuteDraft === '' || minuteDraft == null && minuteShown === '')) {
      hourRef.current?.focus();
    }
  };

  const holdFocus = (e) => e.preventDefault();

  const field = (which) => {
    const isHour = which === 'hour';
    return (
      <span className="date-picker__time-spin">
        <input
          ref={isHour ? hourRef : minuteRef}
          className="date-picker__time-part"
          inputMode="numeric"
          autoComplete="off"
          aria-label={isHour ? 'Hour' : 'Minute'}
          placeholder={isHour ? 'HH' : 'MM'}
          maxLength={2}
          value={isHour ? (hourDraft != null ? hourDraft : hourShown) : (minuteDraft != null ? minuteDraft : minuteShown)}
          onChange={isHour ? onHourChange : onMinuteChange}
          onFocus={(e) => e.target.select()}
          onBlur={() => {
            if (skipBlurRef.current) {
              skipBlurRef.current = false;
              return;
            }
            if (isHour) {
              if (hourDraft == null) return;
              const hours = hourDraft === '' ? null : Math.min(23, Number(hourDraft));
              setHourDraft(null);
              if (hours != null) commit(hours, null);
              return;
            }
            if (minuteDraft == null) return;
            const minutes = minuteDraft === '' ? null : Math.min(59, Number(minuteDraft));
            setMinuteDraft(null);
            if (minutes != null) commit(null, minutes);
          }}
          onKeyDown={onKeyDown(which)}
        />
        <span className="date-picker__time-steps">
          <button
            type="button"
            className="date-picker__time-step date-picker__time-step--up"
            aria-label={isHour ? 'Increase hour' : 'Increase minute'}
            tabIndex={-1}
            onMouseDown={holdFocus}
            onClick={() => bump(which, 1)}
          />
          <button
            type="button"
            className="date-picker__time-step date-picker__time-step--down"
            aria-label={isHour ? 'Decrease hour' : 'Decrease minute'}
            tabIndex={-1}
            onMouseDown={holdFocus}
            onClick={() => bump(which, -1)}
          />
        </span>
      </span>
    );
  };

  return (
    <div className="date-picker__time" role="group" aria-label="Time">
      <span className="date-picker__time-label">Time</span>
      {field('hour')}
      <span className="date-picker__time-colon" aria-hidden="true">:</span>
      {field('minute')}
    </div>
  );
}
